import { useState } from 'react';
import { getStageDisplayLabel } from '../../../utils/commonServices';
import { Button, Menu, MenuItem, CircularProgress } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import '@fontsource/rubik';

/* ─── CSV helpers ─────────────────────────────────────────────── */
function escapeCell(val) {
  return `"${String(val ?? '').replace(/"/g, '""')}"`;
}

function buildCSV(dashboardData) {
  const { summary, projectStatus, monthlyOutput, teamBreakdown, videoTypes } = dashboardData || {};
  const rows = [];

  rows.push(['=== Account Summary ===']);
  rows.push(['Organization',      summary?.org_name || '']);
  rows.push(['Contract Type',     summary?.contract_type || '']);
  rows.push(['Contract Period',   `${summary?.contract_start || ''} – ${summary?.contract_end || ''}`]);
  rows.push(['Renewal Date',      summary?.renewal_date || '']);
  if (summary?.credit_based) {
    rows.push(['Video Credits Total',     summary?.credits_total ?? '']);
    rows.push(['Video Credits Used',      summary?.credits_used ?? '']);
    rows.push(['Video Credits Remaining', summary?.credits_remaining ?? '']);
  } else {
    rows.push(['Videos Total',      summary?.videos_total ?? '']);
    rows.push(['Videos Used',       summary?.videos_used ?? '']);
    rows.push(['Videos Remaining',  (summary?.videos_total ?? 0) - (summary?.videos_used ?? 0)]);
  }
  rows.push(['Quick Turns Total', summary?.quick_turns_total ?? '']);
  rows.push(['Quick Turns Used',  summary?.quick_turns_used ?? '']);
  rows.push(['Quick Turns Remaining', (summary?.quick_turns_total ?? 0) - (summary?.quick_turns_used ?? 0)]);
  rows.push([]);

  rows.push(['=== Project Status ===']);
  rows.push(['Stage', 'Count']);
  (projectStatus?.stages || [])
    .filter((s) => s.stage !== 'Pre-Production')
    .forEach((s) => rows.push([getStageDisplayLabel(s.stage), s.count ?? 0]));
  rows.push([]);

  rows.push(['=== Monthly Output ===']);
  rows.push(['Month', 'Year', 'Videos', 'Quick Turns', 'Total']);
  (monthlyOutput?.months || []).forEach((m) => {
    rows.push([m.month, m.year ?? '', m.videos, m.quick_turns, m.videos + m.quick_turns]);
  });
  rows.push(['TOTAL', '', '', '', monthlyOutput?.total_delivered ?? '']);
  rows.push([]);

  rows.push(['=== Team Breakdown ===']);
  rows.push(['Team', 'Videos']);
  (teamBreakdown?.teams || []).forEach((t) => rows.push([t.name, t.count]));
  rows.push([]);

  rows.push(['=== Video Types ===']);
  rows.push(['Type', 'Count']);
  (videoTypes?.video_types || []).forEach((v) => rows.push([v.name, v.count]));

  return rows.map((r) => r.map(escapeCell).join(',')).join('\n');
}

function downloadBlob(blob, filename) {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ─── PDF export (tables only) ────────────────────────────────── */
async function exportTablePDF(dashboardData) {
  const { default: jsPDF } = await import('jspdf');

  const pdf   = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const mg    = 14;
  const contentW = pageW - 2 * mg;

  const addBg = () => {
    pdf.setFillColor(246, 246, 244);
    pdf.rect(0, 0, pageW, pageH, 'F');
  };

  const addHeader = () => {
    pdf.setFillColor(8, 35, 27);
    pdf.rect(0, 0, pageW, 18, 'F');
    pdf.setTextColor(244, 244, 241);
    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Account Dashboard Report', mg, 12);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(200, 220, 200);
    pdf.text(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      pageW - mg, 12, { align: 'right' },
    );
  };

  addBg();
  addHeader();
  const tblW = contentW;
  let y = 26;

  const drawSection = (title, colHeaders, rows, colRatios) => {
    const colWidths = colRatios.map((r) => r * tblW);
    const rowH      = 6.5;
    const headerH   = 7;

    if (y + headerH + rows.length * rowH + 12 > pageH - mg) {
      pdf.addPage();
      addBg();
      y = mg;
    }

    /* section label */
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(8, 35, 27);
    pdf.text(title, mg, y + 4.5);
    y += 9;

    /* header row */
    pdf.setFillColor(8, 35, 27);
    pdf.rect(mg, y, tblW, headerH, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    let x = mg + 3;
    colHeaders.forEach((h, i) => {
      pdf.text(String(h), x, y + 4.8);
      x += colWidths[i];
    });
    const dataStartY = y + headerH;
    y += headerH;

    /* data rows */
    rows.forEach((row, ri) => {
      if (y + rowH > pageH - mg) {
        pdf.addPage();
        addBg();
        y = mg;
      }
      pdf.setFillColor(ri % 2 === 0 ? 255 : 240, ri % 2 === 0 ? 255 : 245, ri % 2 === 0 ? 255 : 240);
      pdf.rect(mg, y, tblW, rowH, 'F');
      pdf.setTextColor(40, 40, 49);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', row[0] === 'TOTAL' ? 'bold' : 'normal');
      x = mg + 3;
      row.forEach((cell, i) => {
        pdf.text(String(cell ?? ''), x, y + 4.3);
        x += colWidths[i];
      });
      pdf.setDrawColor(220, 228, 220);
      pdf.line(mg, y + rowH, mg + tblW, y + rowH);
      y += rowH;
    });

    /* outer border */
    pdf.setDrawColor(180, 200, 180);
    pdf.rect(mg, dataStartY - headerH, tblW, headerH + rows.length * rowH, 'S');
    y += 10;
  };

  const { summary, projectStatus, monthlyOutput, teamBreakdown, videoTypes } = dashboardData || {};

  drawSection(
    'Account Summary',
    ['Field', 'Value'],
    [
      ['Organization',          summary?.org_name || '—'],
      ['Contract Type',         summary?.contract_type || '—'],
      ['Contract Period',       `${summary?.contract_start || ''} – ${summary?.contract_end || ''}`],
      ['Renewal Date',          summary?.renewal_date || '—'],
      ...(summary?.credit_based ? [
        ['Video Credits Total',     String(summary?.credits_total ?? '—')],
        ['Video Credits Used',      String(summary?.credits_used ?? '—')],
        ['Video Credits Remaining', String(summary?.credits_remaining ?? '—')],
      ] : [
        ['Videos Total',     String(summary?.videos_total ?? '—')],
        ['Videos Used',      String(summary?.videos_used ?? '—')],
        ['Videos Remaining', String((summary?.videos_total ?? 0) - (summary?.videos_used ?? 0))],
      ]),
      ['Quick Turns Total',     String(summary?.quick_turns_total ?? '—')],
      ['Quick Turns Used',      String(summary?.quick_turns_used ?? '—')],
      ['Quick Turns Remaining', String((summary?.quick_turns_total ?? 0) - (summary?.quick_turns_used ?? 0))],
    ],
    [0.35, 0.65],
  );

  drawSection(
    'Project Status',
    ['Stage', 'Count'],
    (projectStatus?.stages || [])
      .filter((s) => s.stage !== 'Pre-Production')
      .map((s) => [getStageDisplayLabel(s.stage), String(s.count ?? 0)]),
    [0.6, 0.4],
  );

  drawSection(
    'Monthly Output',
    ['Month', 'Year', 'Videos', 'Quick Turns', 'Total'],
    [
      ...(monthlyOutput?.months || []).map((m) => [
        m.month,
        String(m.year ?? ''),
        String(m.videos),
        String(m.quick_turns),
        String(m.videos + m.quick_turns),
      ]),
      ['TOTAL', '', '', '', String(monthlyOutput?.total_delivered ?? '')],
    ],
    [0.22, 0.1, 0.2, 0.24, 0.24],
  );

  if ((teamBreakdown?.teams || []).length > 0) {
    drawSection(
      'Team Breakdown',
      ['Team', 'Videos'],
      (teamBreakdown.teams || []).map((t) => [t.name, String(t.count)]),
      [0.7, 0.3],
    );
  }

  if ((videoTypes?.video_types || []).length > 0) {
    drawSection(
      'Video Types',
      ['Type', 'Count'],
      (videoTypes.video_types || []).map((v) => [v.name, String(v.count)]),
      [0.7, 0.3],
    );
  }

  pdf.save(`account_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/* ─── Component ───────────────────────────────────────────────── */
export default function ExportButton({ dashboardData, isLoading }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading]   = useState(null); // 'pdf' | 'csv' | null
  const open = Boolean(anchorEl);
  const disabled = isLoading || !dashboardData;

  const handleExportPDF = async () => {
    setAnchorEl(null);
    setLoading('pdf');
    try {
      await exportTablePDF(dashboardData);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setLoading(null);
    }
  };

  const handleExportCSV = () => {
    setAnchorEl(null);
    setLoading('csv');
    try {
      const csv  = buildCSV(dashboardData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `account_dashboard_${date}.csv`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        endIcon={
          loading
            ? <CircularProgress size={8} sx={{ color: '#08231B' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 11 }} />
        }
        onClick={(e) => !loading && setAnchorEl(e.currentTarget)}
        sx={{
          fontFamily: 'Rubik',
          fontWeight: 500,
          fontSize: '12px !important',
          textTransform: 'none',
          color: '#08231B',
          borderColor: '#d0d4d0',
          borderRadius: '16px',
          px: 1.5,
          py: 0.25,
          minWidth: 0,
          minHeight: 0,
          height: 30,
          lineHeight: 1,
          '& .MuiButton-endIcon': { ml: 0.3 },
          '&:hover': { borderColor: '#08231B', background: 'rgba(8,35,27,0.04)' },
        }}
      >
        {loading === 'pdf' ? 'PDF…' : loading === 'csv' ? 'CSV…' : 'Export'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: -6, horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            border: '1px solid #E9EBE9',
            boxShadow: '0 4px 20px rgba(8,35,27,0.1)',
            minWidth: 180,
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleExportPDF}
          sx={{ fontFamily: 'Rubik', fontSize: '13px', fontWeight: 500, gap: 1.5, py: 1 }}
        >
          <PictureAsPdfOutlinedIcon sx={{ fontSize: 17, color: '#6b7470' }} />
          Export as PDF
        </MenuItem>
        <MenuItem
          onClick={handleExportCSV}
          sx={{ fontFamily: 'Rubik', fontSize: '13px', fontWeight: 500, gap: 1.5, py: 1 }}
        >
          <TableChartOutlinedIcon sx={{ fontSize: 17, color: '#6b7470' }} />
          Export as CSV
        </MenuItem>
      </Menu>
    </>
  );
}
